import { useCallback, useEffect, useRef, useState } from 'react'

import { getSignedUrl, getUploadStatus, UploadStatus } from '../../api/media/secure-upload'

type UseSecureFileState = {
  status: UploadStatus | null
  url: string | null
  isLoading: boolean
}

const TERMINAL_STATUSES: UploadStatus[] = ['READY', 'INFECTED', 'FAILED']
const POLL_INTERVAL_MS = 3000

export const useSecureFile = (uploadId?: string) => {
  const [state, setState] = useState<UseSecureFileState>({
    status: null,
    url: null,
    isLoading: false
  })

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const isFetchingSignedUrl = useRef(false)

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  const poll = useCallback(
    async (id: string) => {
      try {
        const { status } = await getUploadStatus(id)

        if (status === 'READY') {
          stopPolling()
          // Guard: only one getSignedUrl call even if poll fires concurrently
          if (isFetchingSignedUrl.current) return
          isFetchingSignedUrl.current = true
          const { url } = await getSignedUrl(id)
          setState({ status, url, isLoading: false })
          return
        }

        const isTerminal = TERMINAL_STATUSES.includes(status)
        setState({ status, url: null, isLoading: !isTerminal })
        if (isTerminal) stopPolling()
      } catch {
        setState((prev) => ({ ...prev, isLoading: false }))
        stopPolling()
      }
    },
    [stopPolling]
  )

  useEffect(() => {
    if (!uploadId) return

    isFetchingSignedUrl.current = false
    poll(uploadId)
    intervalRef.current = setInterval(() => poll(uploadId), POLL_INTERVAL_MS)

    return () => stopPolling()
  }, [uploadId, poll, stopPolling])

  return {
    status: state.status,
    url: state.url,
    isLoading: state.isLoading,
    setUrl: (url: string) => setState((prev) => ({ ...prev, url }))
  }
}
